import { Controller } from '@hotwired/stimulus';
import { buildJsonHeaders } from 'utils/csrf_helpers';
import consumer from 'channels/consumer';

export default class extends Controller {
  static targets = [
    'feed',
    'emailList',
    'emailEmpty',
    'emailBadge',
    'chatInput',
    'chatSection',
    'statusBadge',
    'workingIndicator',
    'playQueue',
    'sendButton',
    'emailTab',
    'emailPanel',
    'sentList',
    'sentLoadMore',
    'sentRefresh',
    'trainingToggle',
    'trainingPanel',
    'trainingNotes',
    'trainingFlash',
    'learnedPatterns',
  ];

  static values = {
    teamId: Number,
  };

  connect() {
    this.subscribeToChannel();
    this.workingTimeout = null;

    // Auto-scroll activity feed to bottom (newest messages)
    if (this.hasFeedTarget) {
      this.feedTarget.scrollTop = this.feedTarget.scrollHeight;
    }
  }

  disconnect() {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = null;
    }
    if (this.workingTimeout) clearTimeout(this.workingTimeout);
  }

  // --- ActionCable ---

  subscribeToChannel() {
    const controller = this;

    this.subscription = consumer.subscriptions.create(
      { channel: 'SalesTeamChannel', sales_team_id: this.teamIdValue },
      {
        received(data) {
          if (data.type === 'activity') {
            controller.onActivity(data.html);
          }
          if (data.type === 'email_card') {
            controller.onEmailCard(data.html);
          }
          if (data.type === 'play_queue' || data.type === 'play_card') {
            controller.onPlayUpdate();
          }
          if (data.type === 'team_status') {
            controller.onTeamStatus(data.status);
          }
        }
      }
    );
  }

  onActivity(html) {
    if (!this.hasFeedTarget) return;
    this.feedTarget.insertAdjacentHTML('beforeend', html);
    this.feedTarget.scrollTop = this.feedTarget.scrollHeight;
  }

  onEmailCard(html) {
    if (!this.hasEmailListTarget) return;
    // Remove empty state if present
    if (this.hasEmailEmptyTarget) {
      this.emailEmptyTarget.remove();
    }
    this.emailListTarget.insertAdjacentHTML('afterbegin', html);
    this.updateEmailBadge();
  }

  onPlayUpdate() {
    // Reload the page to refresh play state (simplest approach)
    window.location.reload();
  }

  onTeamStatus(status) {
    if (status === 'working') {
      this.showWorking();
    } else {
      this.hideWorking();
    }
  }

  // --- Email Tabs ---

  switchEmailTab(event) {
    const tab = event.currentTarget.dataset.tab;

    this.emailTabTargets.forEach(t => {
      t.classList.toggle('ms-email-tab--active', t.dataset.tab === tab);
    });

    this.emailPanelTargets.forEach(p => {
      p.hidden = p.dataset.tab !== tab;
    });
  }

  async loadMoreSent() {
    const button = this.sentLoadMoreTarget;
    if (!button || !this.hasSentListTarget) return;

    const offset = this.sentListTarget.childElementCount;
    button.disabled = true;
    button.textContent = 'Loading…';

    try {
      const response = await fetch(`/mobile-sales/sent.json?offset=${offset}`, {
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) throw new Error('Failed to load');

      const data = await response.json();

      data.html.forEach(html => {
        this.sentListTarget.insertAdjacentHTML('beforeend', html);
      });

      if (data.has_more) {
        button.disabled = false;
        button.textContent = 'Load more';
      } else {
        button.remove();
      }
    } catch (error) {
      button.disabled = false;
      button.textContent = 'Retry';
      console.error('Load more sent failed:', error);
    }
  }

  async refreshSent() {
    if (!this.hasSentListTarget || !this.hasSentRefreshTarget) return;

    const button = this.sentRefreshTarget;
    button.disabled = true;
    button.textContent = 'Refreshing…';

    try {
      const response = await fetch('/mobile-sales/sent.json?offset=0', {
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) throw new Error('Failed to refresh');

      const data = await response.json();

      this.sentListTarget.innerHTML = '';
      data.html.forEach(html => {
        this.sentListTarget.insertAdjacentHTML('beforeend', html);
      });

      // Show or hide load more button
      if (this.hasSentLoadMoreTarget) {
        if (data.has_more) {
          this.sentLoadMoreTarget.hidden = false;
          this.sentLoadMoreTarget.disabled = false;
          this.sentLoadMoreTarget.textContent = 'Load more';
        } else {
          this.sentLoadMoreTarget.hidden = true;
        }
      }

      button.textContent = 'Refresh';
      button.disabled = false;
    } catch (error) {
      button.textContent = 'Retry';
      button.disabled = false;
      console.error('Refresh sent failed:', error);
    }
  }

  // --- Email Actions ---

  async approveEmail(event) {
    const button = event.currentTarget;
    const emailId = button.dataset.emailId;
    const card = document.getElementById(`ms-email-card-${emailId}`);
    if (!card) return;

    const sigInput = card.querySelector('.ms-email-card__sig-id');
    const signatureId = sigInput ? sigInput.value : null;

    button.disabled = true;
    button.textContent = 'Approving…';

    try {
      const body = { email_id: emailId };
      if (signatureId) body.signature_id = signatureId;

      const response = await fetch('/sales_team/approve_email.json', {
        method: 'POST',
        headers: buildJsonHeaders(),
        body: JSON.stringify(body),
      });

      if (!response.ok) throw new Error('Failed to approve');

      card.classList.add('ms-email-card--approved');
      setTimeout(() => {
        card.remove();
        this.updateEmailBadge();
      }, 1500);
    } catch (error) {
      button.disabled = false;
      button.textContent = 'Approve';
      console.error('Approve failed:', error);
    }
  }

  async rejectEmail(event) {
    const button = event.currentTarget;
    const emailId = button.dataset.emailId;
    const card = document.getElementById(`ms-email-card-${emailId}`);
    if (!card) return;

    if (!confirm('Reject this email? It will be discarded.')) return;

    button.disabled = true;
    button.textContent = 'Rejecting…';

    try {
      const response = await fetch('/sales_team/reject_email.json', {
        method: 'POST',
        headers: buildJsonHeaders(),
        body: JSON.stringify({ email_id: emailId }),
      });

      if (!response.ok) throw new Error('Failed to reject');

      card.classList.add('ms-email-card--rejected');
      setTimeout(() => {
        card.remove();
        this.updateEmailBadge();
      }, 500);
    } catch (error) {
      button.disabled = false;
      button.textContent = 'Reject';
      console.error('Reject failed:', error);
    }
  }

  editEmail(event) {
    const button = event.currentTarget;
    const emailId = button.dataset.emailId;
    const card = document.getElementById(`ms-email-card-${emailId}`);
    if (!card) return;

    const subjectEl = card.querySelector('[data-field="subject"]');
    const bodyEl = card.querySelector('[data-field="body"]');
    const actionsEl = card.querySelector('.ms-email-card__actions');

    const currentSubject = button.dataset.emailSubject;
    const currentBody = bodyEl.innerHTML;

    // Switch to edit mode
    subjectEl.innerHTML = `<input type="text" class="ms-email-edit__subject" value="${this.escapeAttr(currentSubject)}">`;
    bodyEl.contentEditable = true;
    bodyEl.classList.add('ms-email-card__body--editing');

    actionsEl.innerHTML = `
      <div class="ms-email-edit__feedback">
        <textarea class="ms-email-edit__comment" placeholder="Feedback for the AI (optional)..." rows="2"></textarea>
      </div>
      <div class="ms-email-card__actions">
        <button class="mci-btn mci-btn--primary ms-email-btn" data-action="mobile-sales#saveEmail" data-email-id="${emailId}">Save</button>
        <button class="mci-btn mci-btn--ghost ms-email-btn" data-action="mobile-sales#cancelEdit">Cancel</button>
      </div>
    `;
  }

  async saveEmail(event) {
    const button = event.currentTarget;
    const emailId = button.dataset.emailId;
    const card = document.getElementById(`ms-email-card-${emailId}`);
    if (!card) return;

    const subjectInput = card.querySelector('.ms-email-edit__subject');
    const bodyEl = card.querySelector('[data-field="body"]');
    const commentEl = card.querySelector('.ms-email-edit__comment');

    button.disabled = true;
    button.textContent = 'Saving…';

    try {
      const response = await fetch('/sales_team/update_email.json', {
        method: 'PATCH',
        headers: buildJsonHeaders(),
        body: JSON.stringify({
          email_id: emailId,
          subject: subjectInput.value,
          body: bodyEl.innerHTML,
          owner_comment: commentEl?.value || '',
        }),
      });

      if (!response.ok) throw new Error('Failed to save');

      // Reload to show updated card cleanly
      window.location.reload();
    } catch (error) {
      button.disabled = false;
      button.textContent = 'Save';
      console.error('Save failed:', error);
    }
  }

  cancelEdit() {
    window.location.reload();
  }

  // --- Chat ---

  onChatInput() {
    if (!this.hasSendButtonTarget || !this.hasChatInputTarget) return;
    const hasText = this.chatInputTarget.value.trim().length > 0;
    this.sendButtonTarget.classList.toggle('ms-chat__send--active', hasText);
  }

  onChatKeydown(event) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendChat();
    }
  }

  async sendChat() {
    if (!this.hasChatInputTarget) return;

    const message = this.chatInputTarget.value.trim();
    if (!message) return;

    this.chatInputTarget.value = '';
    if (this.hasSendButtonTarget) {
      this.sendButtonTarget.classList.remove('ms-chat__send--active');
    }

    // Optimistic render
    if (this.hasFeedTarget) {
      const html = `
        <div class="ms-activity-item ms-activity--owner_message">
          <div class="ms-activity-item__header">
            <span class="ms-role-badge ms-role--owner">You</span>
            <span class="ms-activity-item__time">just now</span>
          </div>
          <div class="ms-activity-item__content"><p>${this.escapeHtml(message)}</p></div>
        </div>
      `;
      this.feedTarget.insertAdjacentHTML('beforeend', html);
      this.feedTarget.scrollTop = this.feedTarget.scrollHeight;
    }

    this.showWorking();

    try {
      await fetch('/sales_team/chat.json', {
        method: 'POST',
        headers: buildJsonHeaders(),
        body: JSON.stringify({ message }),
      });
    } catch (error) {
      console.error('Chat failed:', error);
    }
  }

  // --- Team Controls ---

  async runPlays() {
    if (!confirm('Start the sales team? The Manager will analyze your studio and create plays.')) return;

    try {
      await fetch('/sales_team/run_plays.json', {
        method: 'POST',
        headers: buildJsonHeaders(),
      });
      this.showWorking();
      this.updateStatusBadge('hired');
    } catch (error) {
      console.error('Run plays failed:', error);
    }
  }

  async pauseTeam() {
    try {
      await fetch('/sales_team/pause.json', {
        method: 'POST',
        headers: buildJsonHeaders(),
      });
      this.hideWorking();
      this.updateStatusBadge('paused');
      window.location.reload();
    } catch (error) {
      console.error('Pause failed:', error);
    }
  }

  async resumeTeam() {
    try {
      await fetch('/sales_team/resume.json', {
        method: 'POST',
        headers: buildJsonHeaders(),
      });
      this.showWorking();
      this.updateStatusBadge('hired');
      window.location.reload();
    } catch (error) {
      console.error('Resume failed:', error);
    }
  }

  async resetTeam() {
    if (!confirm('This will clear all pending opportunities and draft emails. Continue?')) return;

    try {
      await fetch('/sales_team/reset.json', {
        method: 'POST',
        headers: buildJsonHeaders(),
      });
      window.location.reload();
    } catch (error) {
      console.error('Reset failed:', error);
    }
  }

  // --- Training ---

  toggleTraining() {
    if (!this.hasTrainingPanelTarget) return;
    const panel = this.trainingPanelTarget;
    panel.hidden = !panel.hidden;

    if (this.hasTrainingToggleTarget) {
      const arrow = this.trainingToggleTarget.querySelector('.ms-section-toggle__arrow');
      if (arrow) arrow.innerHTML = panel.hidden ? '&darr;' : '&uarr;';
    }
  }

  async saveTraining() {
    if (!this.hasTrainingNotesTarget) return;

    const notes = this.trainingNotesTarget.value;

    try {
      const response = await fetch('/mobile-sales/training.json', {
        method: 'PATCH',
        headers: buildJsonHeaders(),
        body: JSON.stringify({ training_notes: notes }),
      });

      if (!response.ok) throw new Error('Failed to save');

      const data = await response.json();

      // Update learned patterns if returned
      if (this.hasLearnedPatternsTarget && data.learned_patterns) {
        this.learnedPatternsTarget.innerHTML = `<pre class="ms-training__patterns">${this.escapeHtml(data.learned_patterns)}</pre>`;
      }

      // Update the "last updated" timestamp
      if (data.learned_patterns_updated_at) {
        const metaEl = this.element.querySelector('.ms-training__meta');
        if (metaEl) {
          metaEl.textContent = metaEl.textContent.replace(/Updated.*ago\./, 'Updated just now.');
          if (!metaEl.textContent.includes('Updated')) {
            metaEl.textContent += ' Updated just now.';
          }
        }
      }

      this.showTrainingFlash('Training notes saved.');
    } catch (error) {
      this.showTrainingFlash('Unable to save. Please try again.', true);
      console.error('Save training failed:', error);
    }
  }

  showTrainingFlash(message, isError = false) {
    if (!this.hasTrainingFlashTarget) return;
    const flash = this.trainingFlashTarget;
    flash.textContent = message;
    flash.hidden = false;
    flash.className = `ms-training__flash ${isError ? 'ms-training__flash--error' : 'ms-training__flash--success'}`;
    setTimeout(() => { flash.hidden = true; }, 3000);
  }

  // --- UI Helpers ---

  showWorking() {
    if (this.hasWorkingIndicatorTarget) {
      this.workingIndicatorTarget.hidden = false;
    }
    if (this.workingTimeout) clearTimeout(this.workingTimeout);
    this.workingTimeout = setTimeout(() => this.hideWorking(), 5 * 60 * 1000);
  }

  hideWorking() {
    if (this.hasWorkingIndicatorTarget) {
      this.workingIndicatorTarget.hidden = true;
    }
    if (this.workingTimeout) {
      clearTimeout(this.workingTimeout);
      this.workingTimeout = null;
    }
  }

  updateStatusBadge(status) {
    if (!this.hasStatusBadgeTarget) return;
    this.statusBadgeTarget.textContent = status.charAt(0).toUpperCase() + status.slice(1);
    this.statusBadgeTarget.className = `ms-status-badge ms-status-badge--${status}`;
  }

  updateEmailBadge() {
    if (!this.hasEmailBadgeTarget || !this.hasEmailListTarget) return;
    const count = this.emailListTarget.querySelectorAll('.ms-email-card').length;
    this.emailBadgeTarget.textContent = count;
    this.emailBadgeTarget.style.display = count > 0 ? '' : 'none';
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  escapeAttr(text) {
    return text.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/'/g, '&#39;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }
}
